import { useEffect } from 'react';
import './PsychicFangsAnimation.css';

/**
 * PROFESSIONAL PSYCHIC FANGS ANIMATION
 * Psychic-type physical move - bites with psychic-infused fangs
 * 
 * Key Features:
 * - Psychic-type physical attack (85 base power, 100% accuracy)
 * - Destroys Light Screen and Reflect
 * - Psychic energy dash with fang bite impact
 * - Professional gaming quality with psychic aura and fang effects
 */
export default function PsychicFangsAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Psychic Energy Charge (0-400ms)
    playerEl.classList.add('psychic-fangs-user-charge');

    const psychicAura = document.createElement('div');
    psychicAura.className = 'psychic-fangs-aura';
    psychicAura.style.left = `${playerX - 60}px`;
    psychicAura.style.top = `${playerY - 60}px`;
    container.appendChild(psychicAura);
    elements.push(psychicAura);

    // Psychic energy particles
    for (let i = 0; i < 8; i++) {
      const psy = document.createElement('div');
      psy.className = 'psychic-fangs-energy';
      const angle = (i / 8) * 360;
      const radius = 50;
      psy.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 5}px`;
      psy.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 5}px`;
      psy.style.animationDelay = `${i * 40}ms`;
      container.appendChild(psy);
      elements.push(psy);
    }

    // PHASE 2: Psychic Dash (400-700ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('psychic-fangs-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.8}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.8}px`);

      // Psychic trail
      for (let i = 0; i < 8; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'psychic-fangs-trail';
          const progress = i / 8;
          trail.style.left = `${playerX + dx * progress * 0.8 + (Math.random() - 0.5) * 15 - 6}px`;
          trail.style.top = `${playerY + dy * progress * 0.8 + (Math.random() - 0.5) * 15 - 6}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 25);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Fang Bite Impact (700-1200ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('psychic-fangs-opponent-hit');
      playerEl.classList.remove('psychic-fangs-user-dash');

      // Psychic fang visual (two fangs closing)
      const fangTop = document.createElement('div');
      fangTop.className = 'psychic-fangs-fang-top';
      fangTop.style.left = `${opponentX - 30}px`;
      fangTop.style.top = `${opponentY - 50}px`;
      container.appendChild(fangTop);
      elements.push(fangTop);

      const fangBottom = document.createElement('div');
      fangBottom.className = 'psychic-fangs-fang-bottom';
      fangBottom.style.left = `${opponentX - 30}px`;
      fangBottom.style.top = `${opponentY + 10}px`;
      container.appendChild(fangBottom);
      elements.push(fangBottom);

      // Psychic impact flash
      const impact = document.createElement('div');
      impact.className = 'psychic-fangs-impact';
      impact.style.left = `${opponentX - 80}px`;
      impact.style.top = `${opponentY - 80}px`;
      container.appendChild(impact);
      elements.push(impact);

      // Psychic shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'psychic-fangs-shockwave';
          sw.style.left = `${opponentX - 60}px`;
          sw.style.top = `${opponentY - 60}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 80);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Psychic burst particles
      for (let i = 0; i < 14; i++) {
        const burstAngle = (i / 14) * 360;
        const burstDist = 45 + Math.random() * 35;
        const burst = document.createElement('div');
        burst.className = 'psychic-fangs-burst';
        burst.style.left = `${opponentX - 4}px`;
        burst.style.top = `${opponentY - 4}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 15}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }
    }, 700);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Settle (1200-1500ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('psychic-fangs-user-charge');
    }, 1200);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1500ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('psychic-fangs-user-charge', 'psychic-fangs-user-dash');
      opponentEl.classList.remove('psychic-fangs-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1500);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('psychic-fangs-user-charge', 'psychic-fangs-user-dash');
      opponentEl.classList.remove('psychic-fangs-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

PsychicFangsAnimation.moveType = 'psychic';
